import React, { useState, useEffect, createContext } from "react";
import twitch from "twitch-ext";
import appConfig from "appConfig";
import extensionQueryParams from "../utils/twitchQueryParams";
import { twitchHelix } from "../services/api";
import { Channel } from "../interfaces/twitch-helix/Channel";

export interface TwitchContext {
  auth: twitch.Authorized;
  isBitsEnabled: boolean;
  isVisible: boolean;
  channel: Channel | undefined;
}

export const TwitchAuthContext = createContext({} as TwitchContext);

interface TwitchAuth {
  children: React.ReactNode;
}

export default function TwitchContext({ children }: TwitchAuth): JSX.Element | null {
  const [auth, setAuth] = useState<twitch.Authorized | null>(null);
  const [isBitsEnabled, setIsBitsEnabled] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [channel, setChannel] = useState<Channel | undefined>(undefined);

  useEffect(() => {
    async function fetchChannelData(): Promise<void> {
      if (!auth?.helixToken || !auth.channelId) {
        return;
      }

      twitchHelix.http.addHeader("Authorization", `Extension ${auth.helixToken}`);
      twitchHelix.http.addHeader("Client-id", auth.clientId);
      const channelsResponse = await twitchHelix.getChannelInfo(auth.channelId);
      const channel = channelsResponse.data[0];
      if (channel) {
        setChannel(channel);
      }
    }

    fetchChannelData();
  }, [auth?.helixToken, auth?.channelId, auth?.clientId]);

  useEffect(() => {
    twitch.onAuthorized((auth) => {
      setAuth(auth);
    });

    twitch.onVisibilityChanged((visibility: boolean) => {
      setIsVisible(visibility);
    });
  }, []);

  useEffect(() => {
    let enableView = extensionQueryParams.state !== "released" || twitch.features.isBitsEnabled;
    if (appConfig.FORCED_BITS_UNAVAILABILITY_ENABLED) {
      enableView = false;
    }

    setIsBitsEnabled(enableView);
  }, [auth]);

  if (auth === null) {
    return null;
  }

  return (
    <TwitchAuthContext.Provider
      value={{
        auth,
        isBitsEnabled,
        isVisible,
        channel,
      }}
    >
      {children}
    </TwitchAuthContext.Provider>
  );
}
